import { formatDate, formatDateTime, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { type Shift } from "@src/interface";
import {
  formatDuration,
  intervalToDuration,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  parseISO,
} from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { UrgencyType } from "../../Shifts/types";
import { type ShiftChip, type ShiftChipData, ShiftChipType } from "./types";

const aTeamChipStyles = {
  color: "#032E68",
  backgroundColor: "#CEECFF",
  "&:hover": {
    backgroundColor: "#BDDFF0",
  },
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: "18px",
  letterSpacing: "1.5px",
  borderRadius: 1.5,
};

const SHIFT_CHIP_TYPE_MAP: Record<ShiftChipType, ShiftChipData> = {
  [ShiftChipType.MAGIC]: {
    type: ShiftChipType.MAGIC,
    color: "accent",
    getTooltip: () =>
      "We increase the pay for Magic Shifts to get them filled 99% of the time, at no additional cost to you.",
  },
  [ShiftChipType.URGENT]: {
    type: ShiftChipType.URGENT,
    color: "warning",
    getTooltip: () =>
      "We significantly increase the pay for Urgent Shifts to quickly find a replacement.",
  },
  [ShiftChipType.WORKER_LEFT_EARLY_REPLACEMENT_WORKER]: {
    type: ShiftChipType.WORKER_LEFT_EARLY_REPLACEMENT_WORKER,
    color: "info",
    getTooltip: () =>
      "This shift is created by a worker left early request. We're looking for a replacement worker.",
  },
  [ShiftChipType.WORKER_LEFT_EARLY]: {
    type: ShiftChipType.WORKER_LEFT_EARLY,
    color: "error",
    getTooltip: () => "The worker left early for this shift.",
  },
  [ShiftChipType.STILL_BOOKABLE]: {
    type: ShiftChipType.STILL_BOOKABLE,
    color: "success",
    getTooltip: (tooltipData) => {
      if (!tooltipData?.extendedBookabilityTo || !tooltipData.facilityTimeZone) {
        return "";
      }

      const extendedBookabilityToFacilityTz = utcToZonedTime(
        tooltipData.extendedBookabilityTo,
        tooltipData.facilityTimeZone
      );

      const timePart = formatTime(extendedBookabilityToFacilityTz);
      let fullTime = timePart;
      if (isToday(tooltipData.extendedBookabilityTo)) {
        fullTime += " today";
      } else if (isTomorrow(tooltipData.extendedBookabilityTo)) {
        fullTime += " tomorrow";
      } else {
        fullTime += ` on ${formatDate(tooltipData.extendedBookabilityTo)}`;
      }

      return `Booking is allowed until ${fullTime}.`;
    },
  },
  [ShiftChipType.ATEAM_EXCLUSIVE_BLOCK]: {
    type: ShiftChipType.ATEAM_EXCLUSIVE_BLOCK,
    color: "info",
    getTooltip: () => "This shift block is bookable only by your A-Team.",
    customStyles: aTeamChipStyles,
  },
};

export function getShiftChipDataFromShiftChip(shiftChip: ShiftChip): ShiftChipData {
  return { ...shiftChip, ...SHIFT_CHIP_TYPE_MAP[shiftChip.type] };
}

const ShiftChipTypeOrder: Record<ShiftChipType, number> = {
  [ShiftChipType.MAGIC]: 0,
  [ShiftChipType.URGENT]: 1,
  [ShiftChipType.STILL_BOOKABLE]: 2,
  [ShiftChipType.ATEAM_EXCLUSIVE_BLOCK]: 3,
  [ShiftChipType.WORKER_LEFT_EARLY_REPLACEMENT_WORKER]: 4,
  [ShiftChipType.WORKER_LEFT_EARLY]: 5,
};

export function sortShiftChipData(shiftChipData: ShiftChipData[]): ShiftChipData[] {
  return shiftChipData.sort((a, b) => ShiftChipTypeOrder[a.type] - ShiftChipTypeOrder[b.type]);
}

export function formatTimeline(shiftStart: Date, shiftEnd: Date, timeZone: string): string {
  // use browser local timezone to convert datetimes to UTC
  const utcShiftStart = zonedTimeToUtc(
    shiftStart,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const utcShiftEnd = zonedTimeToUtc(shiftEnd, Intl.DateTimeFormat().resolvedOptions().timeZone);

  const shiftStartText = formatDateTime(utcShiftStart, { timeZone });
  const shiftEndText = formatDateTime(utcShiftEnd, { timeZone });
  const duration = formatDuration(intervalToDuration({ start: shiftStart, end: shiftEnd }));
  return `${shiftStartText} - ${shiftEndText} (${duration})`;
}

export const shouldShowStillBookableChip = (
  shift: Pick<
    Shift,
    | "deleted"
    | "bookable"
    | "extendBookabilityTo"
    | "isMagicShift"
    | "agentId"
    | "urgency"
    | "start"
    | "end"
  >
): shift is Shift & { extendBookabilityTo: string } => {
  if (shift.deleted ?? (isDefined(shift.bookable) && !shift.bookable)) {
    return false;
  }

  if (!shift.extendBookabilityTo) {
    return false;
  }

  if (shift.isMagicShift) {
    return false;
  }

  if (
    isDefined(shift.agentId) ||
    shift.urgency === UrgencyType.NCNS ||
    shift.urgency === UrgencyType.LATE_CANCELLATION
  ) {
    return false;
  }

  const shiftStart = parseISO(shift.start);
  const extendedBookabilityTo = parseISO(shift.extendBookabilityTo);

  const now = new Date();
  return isBefore(shiftStart, now) && isAfter(extendedBookabilityTo, now);
};

export function shouldShowUrgentChip(parameters: {
  shift: Pick<Shift, "end" | "deleted" | "urgency">;
  isMagicShift: boolean;
}): boolean {
  const { shift, isMagicShift } = parameters;
  // don't show urgent chip if shift has ended
  if (new Date(shift.end) < new Date()) {
    return false;
  }

  // don't show urgent chip if shift is cancelled
  if (shift.deleted) {
    return false;
  }

  return (
    (shift.urgency === UrgencyType.NCNS && !isMagicShift) ||
    shift.urgency === UrgencyType.LATE_CANCELLATION
  );
}

export function shouldShowWorkerLeftEarlyRequestShiftChip(shift: Shift): boolean {
  return isDefined(shift.replacedShiftId);
}

export function shouldShowWorkerLeftEarlyShiftChip(shift: Shift): boolean {
  return isDefined(shift.workerLeftEarlyRequest?.id);
}
